import React, { useEffect, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { BsLightningCharge } from 'react-icons/bs';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { baseURL } from '../helpers/baseURL';
import { headers } from '../helpers/Requests';
import { resolveQuickReplyContent, stripHtmlPreview } from '../helpers/quickReplyVariables';
import { createEmptyQuickReplyComposeState } from '../helpers/quickReplyCompose';
import { showConfirmDialog } from './ItemDelete';
import styles from './QuickRepliesPicker.module.css';

const QuickRepliesPicker = ({
    editorRef,
    ticket,
    disabled = false,
    editorResetKey = 0,
    composeRef,
}) => {
    const user = useSelector((state) => state?.userReducer?.user);
    const workspaceId = user?.current_workspace;
    const [replies, setReplies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [selectedReply, setSelectedReply] = useState(null);
    const isCustomer = user?.isCustomer || user?.is_customer;

    const resetComposeState = () => {
        if (composeRef) {
            composeRef.current = createEmptyQuickReplyComposeState();
        }
    };

    useEffect(() => {
        setSelectedReply(null);
        setReplies([]);
        setLoadError(false);
        resetComposeState();
    }, [ticket?.id, editorResetKey, workspaceId]);

    const loadReplies = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const { data } = await axios.get(`${baseURL}quick-replies/active`, headers);
            setReplies(Array.isArray(data) ? data : []);
        } catch (error) {
            setReplies([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (isOpen) => {
        if (isOpen) {
            loadReplies();
        }
    };

    const applyQuickReply = (reply) => {
        const template = reply.content ?? '';
        const resolved = resolveQuickReplyContent(template, { ticket, user });

        if (composeRef) {
            composeRef.current = {
                draft: { id: reply.id, name: reply.name, template },
                modified: false,
            };
        }

        editorRef.current.setHTML(resolved);
        setSelectedReply({ id: reply.id, name: reply.name });
    };

    const handleSelect = (reply) => {
        if (!editorRef?.current) {
            return;
        }

        const hasExistingContent = editorRef.current.getText?.()?.trim().length > 0;
        if (hasExistingContent) {
            showConfirmDialog({
                title: 'Replace current message?',
                message: 'Replace your current message with this quick reply?',
                confirmLabel: 'Replace',
                cancelLabel: 'Cancel',
                onConfirm: () => applyQuickReply(reply),
            });
            return;
        }

        applyQuickReply(reply);
    };

    if (isCustomer) {
        return null;
    }

    return (
        <Dropdown onToggle={handleToggle} align="end" className={styles.picker}>
            <Dropdown.Toggle
                variant="outline-secondary"
                disabled={disabled}
                className={styles.toggle}
                title={selectedReply?.name ?? 'Quick replies'}
            >
                <BsLightningCharge className="me-1" />
                <span className={styles.toggleLabel}>{selectedReply?.name ?? 'Quick replies'}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu className={styles.menu}>
                {loading && (
                    <div className={styles.menuState}>
                        <Spinner size="sm" animation="border" /> Loading...
                    </div>
                )}

                {!loading && loadError && (
                    <div className={styles.menuState}>
                        Could not load quick replies.
                    </div>
                )}

                {!loading && !loadError && replies.length === 0 && (
                    <div className={styles.menuState}>
                        No active quick replies yet.
                    </div>
                )}

                {!loading && !loadError && replies.map((reply) => {
                    const isSelected = selectedReply?.id === reply.id;
                    return (
                    <Dropdown.Item
                        key={reply.id}
                        onClick={() => handleSelect(reply)}
                        className={styles.menuItem}
                        active={isSelected}
                    >
                        <span className={styles.menuItemName}>{reply.name}</span>
                        <span className={styles.menuItemPreview}>
                            {stripHtmlPreview(reply.content) || '—'}
                        </span>
                    </Dropdown.Item>
                    );
                })}

                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/settings/quick-replies" className={styles.manageLink}>
                    Manage quick replies
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default QuickRepliesPicker;
